import { Box, Link, Typography } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { LeadSourceBadge } from "../../components/LeadSourceBadge.js";
import { VerticalBadge } from "../../components/VerticalBadge.js";
import { leadHeatDisplay, toneChipSx } from "../heatStyles.js";
import { statusLabel } from "../leadStatus.js";
import { verdictMeta, type VerdictMeta } from "../quality.js";
import { UrgencyBadge } from "../UrgencyBadge.js";
import {
  ConvertedBadge,
  DeadlineProofNote,
  FitBadge,
  HeatBadge,
  InCrmBadge,
  RfpDeadlineBadge,
} from "./badges.js";
import { firstSentence } from "./leadTldr.js";
import { ExpandableText, panelSx } from "./primitives.js";

/* ── 1. Hero — identity + confirmation verdict + TL;DR ──────────── */

export function Hero({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element {
  const tldr =
    (lead.agent_summary && lead.agent_summary.trim()) ||
    firstSentence(lead.briefs?.[0]?.why_now_markdown ?? lead.agent_full_reasoning ?? "");
  const verdict = verdictMeta(lead.eval_verdict);
  const confPct = Math.round((lead.confidence ?? 0) * 100);
  return (
    <Box component="section" sx={[panelSx, { p: 3, gap: 1.75 }]}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
          <IdentityMark name={lead.name} />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: "text.primary", overflowWrap: "anywhere" }}>
              {lead.name}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 0.875, fontSize: 12 }}>
              <UrgencyBadge lead={lead} />
              {/* Heat folds in live deadline urgency (see `leadHeatDisplay`) so
                * a warm lead with a closing window reads SUPER HOT / LAST CHANCE. */}
              <HeatBadge display={leadHeatDisplay(lead)} />
              <RfpDeadlineBadge lead={lead} />
              {lead.converted_at ? (
                <ConvertedBadge />
              ) : lead.crm_pushed_at ? (
                <InCrmBadge />
              ) : null}
              {lead.lead_kind === "fit" ? <FitBadge /> : null}
              <VerticalBadge vertical={lead.vertical} />
              {/* Which bot found this lead — both feed the one Leads pipeline. */}
              <LeadSourceBadge source={lead.source} />
              <Box component="span" sx={{ color: "text.disabled" }}>
                {lead.country || "—"} · {statusLabel(lead.status)}
              </Box>
              {lead.domain ? (
                <Link
                  href={`https://${lead.domain}`}
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                  sx={{ borderRadius: 999, border: 1, borderColor: "divider", px: 1, py: 0.25, color: "text.disabled", "&:hover": { color: "text.primary" } }}
                >
                  {lead.domain} ↗
                </Link>
              ) : null}
            </Box>
            <DeadlineProofNote lead={lead} />
          </Box>
        </Box>
        <VerdictBadge meta={verdict} confPct={confPct} />
      </Box>
      {lead.recommended_bundle ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, fontSize: 12 }}>
          <Box component="span" sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            Recommended bundle
          </Box>
          <Box component="span" sx={{ borderRadius: 999, border: 1, borderColor: "divider", bgcolor: "background.default", px: 1.25, py: 0.375, fontWeight: 600, color: "text.primary" }}>
            {lead.recommended_bundle}
          </Box>
        </Box>
      ) : null}
      {tldr ? (
        <ExpandableText collapsedHeight={78}>
          <Typography sx={{ fontSize: 15, lineHeight: 1.65, color: "text.primary" }}>
            {tldr}
          </Typography>
        </ExpandableText>
      ) : null}
    </Box>
  );
}

function IdentityMark({ name }: { name: string }): JSX.Element {
  const letter = (name.trim().charAt(0) || "?").toUpperCase();
  return (
    <Box
      aria-hidden
      sx={{ flexShrink: 0, display: "grid", placeItems: "center", height: 52, width: 52, borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", fontSize: 22, fontWeight: 700, color: "text.secondary" }}
    >
      {letter}
    </Box>
  );
}

function VerdictBadge({ meta, confPct }: { meta: VerdictMeta; confPct: number }): JSX.Element {
  return (
    <Box sx={{ display: "grid", justifyItems: "end", gap: 0.5, flexShrink: 0 }}>
      <Box
        sx={[
          { display: "inline-flex", alignItems: "center", gap: 0.625, borderRadius: 999, border: 1, px: 1.5, py: 0.625, fontSize: 14, fontWeight: 700 },
          toneChipSx(meta.tone),
        ]}
      >
        <Box component="span" aria-hidden sx={{ fontSize: 15 }}>{meta.icon}</Box>
        {meta.label}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, fontSize: 12, color: "text.disabled" }}>
        <Box component="span" sx={{ fontWeight: 700, color: "text.secondary" }}>{confPct}%</Box>
        confidence
      </Box>
      <Box sx={{ fontSize: 11, color: "text.disabled", maxWidth: 240, textAlign: "right" }}>
        {meta.blurb}
      </Box>
    </Box>
  );
}
